// The poster owns the first paint; only the visible, matching video gets a URL.
// startDelay (ms) holds the video request until well after the intro reveals the
// poster, so the large mp4 never starts before LCP (PageSpeed's simulated 4G adds
// every request started before LCP to the LCP estimate).
export function createBannerVideoController(banner, { onReady, onPause, isPaused, startDelay = 0 }) {
	const media = window.matchMedia("(max-width: 768px)");
	let visible = !window.IntersectionObserver;
	const failedPosters = new WeakSet();
	const playingVideos = new WeakSet();
	const introDone = () => window.introFinished === true || !document.querySelector(".intro");
	let delayPassed = startDelay <= 0;
	let delayTimer = null;
	function armDelay() {
		if (delayPassed || delayTimer || !introDone()) return;
		delayTimer = setTimeout(() => {
			delayPassed = true;
			notify();
		}, startDelay);
	}
	const getVideo = (slide) => slide && (
		slide.querySelector(media.matches ? "video.mobile" : "video.desktop") ||
		slide.querySelector("video")
	);
	function updatePoster(slide) {
		const poster = slide && slide.querySelector(".primary-banner-poster");
		if (!poster) return;
		// A paused video retains its frame. Only hide the responsive poster once
		// the video for the current viewport has actually started playing.
		const opacity = playingVideos.has(getVideo(slide)) ? "0" : "1";
		if (poster.style.opacity !== opacity) poster.style.opacity = opacity;
	}
	function canStart(slide) {
		const poster = slide && slide.querySelector(".primary-banner-poster img");
		return visible && !document.hidden && !isPaused() &&
			introDone() && delayPassed &&
			(!poster || (poster.complete && poster.naturalWidth > 0) || failedPosters.has(poster));
	}
	function notify() {
		armDelay();
		if (!visible || document.hidden) onPause();
		else onReady();
	}
	if (banner) {
		banner.querySelectorAll(".primary-banner-poster img").forEach((poster) => {
			poster.addEventListener("load", notify);
			poster.addEventListener("error", () => {
				failedPosters.add(poster);
				notify();
			});
			if (poster.complete && !poster.naturalWidth) failedPosters.add(poster);
		});
		banner.querySelectorAll("video").forEach((video) => {
			const slide = video.closest(".swiper-slide");
			video.addEventListener("playing", () => {
				playingVideos.add(video);
				video.style.opacity = "1";
				updatePoster(slide);
			});
			const restorePoster = () => {
				playingVideos.delete(video);
				video.style.opacity = "0";
				updatePoster(slide);
			};
			video.addEventListener("error", restorePoster);
			video.addEventListener("emptied", restorePoster);
		});
		if (window.IntersectionObserver) {
			const observer = new window.IntersectionObserver(([entry]) => {
				visible = entry.isIntersecting;
				notify();
			});
			observer.observe(banner);
		}
	}
	const switchVideo = () => {
		onPause();
		if (banner) banner.querySelectorAll(".swiper-slide").forEach(updatePoster);
		notify();
	};
	if (media.addEventListener) media.addEventListener("change", switchVideo);
	else if (media.addListener) media.addListener(switchVideo);
	document.addEventListener("visibilitychange", notify);
	window.addEventListener("peroma:intro-complete", notify);
	if (window.introReady) window.introReady.then(notify);
	armDelay();
	return {
		getVideo,
		canStart,
		prepare(video, slide) {
			if (!video || getVideo(slide) !== video || !canStart(slide)) return false;
			if (video.dataset.bannerPoster) video.setAttribute("poster", video.dataset.bannerPoster);
			let changed = false;
			// Cached pages may still contain the previous data-src markup.
			video.querySelectorAll("source[data-banner-src], source[data-src]").forEach((source) => {
				if (!source.hasAttribute("src")) {
					source.setAttribute("src", source.dataset.bannerSrc || source.dataset.src);
					changed = true;
				}
			});
			if (changed) video.load();
			return true;
		},
	};
}
